// Debug utilities for service worker and caching

/**
 * Get comprehensive cache information
 */
export const getCacheInfo = async () => {
  if (!('caches' in window)) {
    return { error: 'Cache API not supported' };
  }

  try {
    const cacheNames = await caches.keys();
    const cacheInfo = {};

    for (const cacheName of cacheNames) {
      const cache = await caches.open(cacheName);
      const keys = await cache.keys();
      cacheInfo[cacheName] = {
        count: keys.length,
        urls: keys.map(req => req.url)
      };
    }

    return cacheInfo;
  } catch (error) {
    return { error: error.message };
  }
};

/**
 * Clear all caches
 */
export const clearAllCaches = async () => {
  if (!('caches' in window)) {
    return false;
  }

  try {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(name => caches.delete(name)));
    console.log('All caches cleared');
    return true;
  } catch (error) {
    console.error('Failed to clear caches:', error);
    return false;
  }
};

/**
 * Check if service worker is registered and active
 */
export const getServiceWorkerStatus = async () => {
  if (!('serviceWorker' in navigator)) {
    return { supported: false };
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    
    if (!registration) {
      return { supported: true, registered: false };
    }

    return {
      supported: true,
      registered: true,
      active: !!registration.active,
      installing: !!registration.installing,
      waiting: !!registration.waiting,
      scope: registration.scope,
      updateViaCache: registration.updateViaCache
    };
  } catch (error) {
    return { supported: true, error: error.message };
  }
};

/**
 * Unregister service worker
 */
export const unregisterServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.unregister();
      console.log('Service worker unregistered');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Failed to unregister service worker:', error);
    return false;
  }
};

/**
 * Force service worker update
 */
export const updateServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.update();
      console.log('Service worker update triggered');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Failed to update service worker:', error);
    return false;
  }
};

/**
 * Log comprehensive debug information
 */
export const debugOfflineCapabilities = async () => {
  console.group('🔧 Offline Capabilities Debug Info');
  
  // Service Worker Status
  console.group('📡 Service Worker');
  const swStatus = await getServiceWorkerStatus();
  console.log(swStatus);
  console.groupEnd();
  
  // Cache Status
  console.group('💾 Cache Storage');
  const cacheInfo = await getCacheInfo();
  console.log(cacheInfo);
  console.groupEnd();
  
  // Network Status
  console.group('🌐 Network');
  console.log('Online:', navigator.onLine);
  console.log('Connection:', navigator.connection ? {
    effectiveType: navigator.connection.effectiveType,
    downlink: navigator.connection.downlink,
    rtt: navigator.connection.rtt
  } : 'Not available');
  console.groupEnd();
  
  // Storage Estimate
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    console.group('📊 Storage Quota');
    const estimate = await navigator.storage.estimate();
    console.log('Used:', Math.round(estimate.usage / 1024 / 1024), 'MB');
    console.log('Quota:', Math.round(estimate.quota / 1024 / 1024), 'MB');
    console.log('Percentage:', Math.round((estimate.usage / estimate.quota) * 100), '%');
    console.groupEnd();
  }
  
  console.groupEnd();
};

// Make debug functions available globally in development
if (process.env.NODE_ENV === 'development') {
  window.debugOffline = {
    getCacheInfo,
    clearAllCaches,
    getServiceWorkerStatus,
    unregisterServiceWorker,
    updateServiceWorker,
    debugOfflineCapabilities
  };
  
  console.log('🔧 Debug utilities available:');
  console.log('  window.debugOffline.debugOfflineCapabilities() - Show all debug info');
  console.log('  window.debugOffline.getCacheInfo() - Get cache details');
  console.log('  window.debugOffline.clearAllCaches() - Clear all caches');
  console.log('  window.debugOffline.getServiceWorkerStatus() - Check SW status');
  console.log('  window.debugOffline.updateServiceWorker() - Force SW update');
}

const debugOfflineUtils = {
  getCacheInfo,
  clearAllCaches,
  getServiceWorkerStatus,
  unregisterServiceWorker,
  updateServiceWorker,
  debugOfflineCapabilities
};

export default debugOfflineUtils;
